import { FABRIC_CAPTURE_THRESHOLDS } from '../constants/thresholds';
import { distanceStateFromCoverage } from './distanceFromCoverage';

/**
 * Combines distance + quick quality heuristics for live guidance.
 *
 * @param {import('../types').FabricRegionMetrics} fabricMetrics
 * @param {import('../types').CaptureQualityScore} quality
 * @param {typeof FABRIC_CAPTURE_THRESHOLDS} [t]
 * @returns {import('../types').CaptureReadinessState}
 */
export function computeCaptureReadiness(fabricMetrics, quality, t = FABRIC_CAPTURE_THRESHOLDS) {
  const lowConf = fabricMetrics.confidence < t.minDetectorConfidenceForReady;
  const dist = distanceStateFromCoverage(fabricMetrics.coverageRatio, t);
  const blurOk = quality.sharpness >= t.minSharpnessThreshold * 0.85;
  const lightOk = quality.brightnessMean >= t.minBrightnessThreshold * 0.92;
  const distanceOk = dist === 'good';

  if (!blurOk || !lightOk || dist === 'too_close' || dist === 'too_far') {
    return 'not_ready';
  }

  if (distanceOk && blurOk && lightOk && !lowConf) {
    return 'ready';
  }

  if ((dist === 'adjust' && fabricMetrics.coverageRatio >= t.tooFarCoverageThreshold) || lowConf) {
    return 'almost_ready';
  }

  return 'not_ready';
}

/**
 * @param {import('../types').CaptureReadinessState} readiness
 * @param {import('../types').DistanceState} distance
 * @returns {import('../types').CaptureGuidanceState}
 */
export function buildGuidanceState(readiness, distance) {
  let hint;
  if (distance === 'too_close') hint = 'Move back until the frame is less full.';
  else if (distance === 'too_far') hint = 'Move closer so the fabric fills more of the guide.';
  else if (distance === 'adjust') hint = 'Fine-tune distance to land in the green zone.';
  else if (readiness === 'ready') hint = 'You can capture now.';
  else if (readiness === 'almost_ready') hint = 'Almost there — check lighting and sharpness.';

  return { distance, readiness, hint };
}
